"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { IconSend } from "@tabler/icons-react";

type Props = {
  onSend: (body: string) => Promise<void> | void;
  onTyping?: () => void;
  disabled?: boolean;
};

export function MessageInput({ onSend, onTyping, disabled }: Props) {
  const t = useTranslations("support_chat");
  const [value, setValue] = useState("");
  const [sending, setSending] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const lastTypingRef = useRef(0);

  function autosize() {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
  }

  async function send() {
    const body = value.trim();
    if (!body || sending || disabled) return;
    setSending(true);
    try {
      await onSend(body);
      setValue("");
      requestAnimationFrame(autosize);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex gap-2 items-end px-3 py-2 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <textarea
        ref={taRef}
        value={value}
        rows={1}
        placeholder={t("input_placeholder")}
        disabled={disabled}
        onChange={(e) => {
          setValue(e.target.value);
          autosize();
          if (onTyping) {
            const now = Date.now();
            if (now - lastTypingRef.current > 1500) {
              lastTypingRef.current = now;
              onTyping();
            }
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        className="flex-1 resize-none bg-transparent text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none max-h-[120px]"
      />
      <button
        type="button"
        onClick={send}
        disabled={!value.trim() || sending || disabled}
        aria-label={t("send")}
        className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-red-500 hover:bg-red-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <IconSend className="h-4 w-4" />
      </button>
    </div>
  );
}
